<template>
  <div class="sub-tier-selection">
    <div class="header-section">
        <h1>Select Your Subscription Tier</h1>
        <p>Large or small, we have a tier to fit your needs.</p>
    </div>

    <div class="card-section">
      <v-row>
        <v-col v-for="tier in subscriptionTiers" :key="tier.tier" cols="12" md="3">
          <v-card
            class="mx-auto"
            max-width="344"
            outlined
            @click="selectTier(tier)"
            :class="{ 'selected-card': selectedTier && selectedTier.tier === tier.tier }"
          >
            <v-card-title class="text-h5">
              {{ tier.tier }}
            </v-card-title>
            <v-card-subtitle>
              <span v-html="tier.price"></span><br>
            </v-card-subtitle>
            <v-card-subtitle class="apps-supported-subtitle">
              <template v-if="tier.tier === 'Enterprise'">
                {{ tier.appsSupported }}
              </template>
              <template v-else>
                Apps Supported: {{ tier.appsSupported }}
              </template>
            </v-card-subtitle>
            <v-card-text>
              <div v-if="tier.features && Object.keys(tier.features).length > 0">
                <div v-if="getFeaturesPlusText(tier.tier)"><strong>{{ getFeaturesPlusText(tier.tier) }}</strong></div>
                <ul>
                  <li v-for="(feature, index) in Object.values(tier.features).slice(0, 3)" :key="index">
                    {{ feature }}
                  </li>
                </ul>
                <div v-if="tier.tier === 'Basic' && tier.anthropometrics && Object.keys(tier.anthropometrics).length > 0">
                  <p class="font-weight-bold mt-2">Anthropometrics:</p>
                  <ul>
                    <li v-for="(anthro, index) in Object.values(tier.anthropometrics)" :key="index">
                      {{ anthro }}
                    </li>
                  </ul>
                </div>
              </div>
            </v-card-text>
            <v-card-actions>
              <v-btn text color="primary">
                Select Plan
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <div class="continue-button-section">
      <v-btn color="primary" large class="str-btn-primary" @click="handleContinue" ref="continueButton" :disabled="!selectedTier">Continue</v-btn>
    </div>

    <h2 class="table-title">Compare Plans</h2>
    <v-table class="compare-table">
      <thead>
        <tr>
          <th class="feature-header"></th>
          <th v-for="tier in compareTableData" :key="tier.tier" class="tier-header">
            {{ tier.tier }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="header in compareTableHeaders" :key="header">
          <th class="feature-name">
            <div class="d-flex align-center">
              {{ header }}
              <v-tooltip v-if="headerTooltipCopy[header]" location="top">
                <template v-slot:activator="{ props }">
                  <img v-if="headerTooltipCopy[header]" src="@/assets/images/icons/info.svg" alt="info icon" class="ml-1" v-bind="props" style="width: 20px; height: 20px;" />
                </template>
                {{ headerTooltipCopy[header] }}
              </v-tooltip>
            </div>
          </th>
          <td v-for="tierData in compareTableData" :key="tierData.tier" class="tier-data">
            <template v-if="typeof tierData[headerToKeyMap[header]] === 'boolean'">
              <img v-if="tierData[headerToKeyMap[header]]" src="@/assets/images/icons/check.svg" alt="Checkmark" class="check-icon" />
            </template>
            <template v-else>
              <div v-html="tierData[headerToKeyMap[header]]"></div>
            </template>
          </td>
        </tr>
      </tbody>
    </v-table>
  </div>
</template>

<script>
import { ref, onMounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-vue';

export default {
  name: "SubscriptionTierSelection",
  setup() {
    const store = useStore();
    const router = useRouter();
    const { getAccessTokenSilently } = useAuth0();

    const subscriptionTiers = ref([
      {
        "tier": "Basic",
        "price": "$149",
        "appsSupported": 1,
        "features": {
          1: "Structure Sensor Scanning",
          2: "Feature Scanning",
          3: "Usage Reporting",
          4: "iOS Update Compatibility"
        },
        "anthropometrics": {
          1: "Landmark Detection, 1st and 5th metatarsal",
          2: "Measurements"
        }
      },
      {
        "tier": "Professional",
        "price": "$349",
        "appsSupported": 1,
        "features": {
          1: "Original Structure Sensor Support",
          2: "Dedicated Account Management",
          3: "Dedicated Onboarding Support"
        }
      },
      {
        "tier": "Premium",
        "price": "$499",
        "appsSupported": 1,
        "features": {
          1: "TrueDepth Scanning",
          2: "SQI (Scan Quality Indicator)",
          3: "App Integration Support 1:1"
        }
      },
      {
        "tier": "Enterprise",
        "price": "<a class='str-btn-primary' href='mailto:developers@structure.io'>Contact Us</a>",
        "appsSupported": "Supports Multiple Applications",
        "features": {
          1: "OMS Capabilities",
          2: "Special SDK Implementation (AR/VR/etc)",
          3: "510K Clearance Support",
          4: "Customer Troubleshoot Support",
          "footnote": "This plan supports a high user base"
        }
      }
    ]);

    const fetchTiers = async () => {
      try {
        const token = await getAccessTokenSilently();
        const headers = { Authorization: `Bearer ${token}` };
        const response = await axios.get(`${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/tiers`, { headers });
        const tiersFromApi = response.data;

        const tierDataMap = new Map(tiersFromApi.map(tier => [tier.name, tier]));

        subscriptionTiers.value = subscriptionTiers.value.map(localTier => {
          const apiTier = tierDataMap.get(localTier.tier);
          if (apiTier) {
            const updated = { ...localTier, stripePriceId: apiTier.stripe_price_id };
            if (apiTier.stripe_metered_price_id) {
              updated.stripeMeteredPriceId = apiTier.stripe_metered_price_id;
            }
            return updated;
          }
          return localTier;
        });

      } catch (error) {
        console.error("Failed to fetch tier data:", error);
      }
    };

    onMounted(() => {
      fetchTiers();
      nextTick(() => {
        window.scrollTo(0, 0);
      });
    });

    const compareTableData = [
      {
        "tier": "Basic",
        "price": "$149",
        "appsSupported": 1,
        "sensorScanning": true,
        "reportingMetrics": true,
        "anthropometrics": true,
        "st01Support": false,
        "accountManagement": false,
        "truedepthScanning": false,
        "lidarScanning": false,
        "sqi": false,
        "specialSdkImplementation": false,
        "510kSupport": false,
        "customerTroubleshooting": false
      },
      {
        "tier": "Professional",
        "price": "$349",
        "appsSupported": 1,
        "sensorScanning": true,
        "reportingMetrics": true,
        "anthropometrics": true,
        "st01Support": true,
        "accountManagement": true,
        "truedepthScanning": false,
        "lidarScanning": false,
        "sqi": false,
        "specialSdkImplementation": false,
        "510kSupport": false,
        "customerTroubleshooting": false
      },
      {
        "tier": "Premium",
        "price": "$499",
        "appsSupported": 1,
        "sensorScanning": true,
        "reportingMetrics": true,
        "anthropometrics": true,
        "st01Support": true,
        "accountManagement": true,
        "truedepthScanning": true,
        "lidarScanning": true,
        "sqi": true,
        "specialSdkImplementation": false,
        "510kSupport": false,
        "customerTroubleshooting": false
      },
      {
        "tier": "Enterprise",
        "price": "<a class='str-btn-primary' href='mailto:developers@structure.io'>Contact Us</a>",
        "appsSupported": "2+",
        "sensorScanning": true,
        "reportingMetrics": true,
        "anthropometrics": true,
        "st01Support": true,
        "accountManagement": true,
        "truedepthScanning": true,
        "lidarScanning": true,
        "sqi": true,
        "specialSdkImplementation": true,
        "510kSupport": true,
        "customerTroubleshooting": true
      }
    ];

    const compareTableHeaders = [
      "Price Per Month",
      "Number of Apps Supported",
      "Sensor Scanning",
      "Reporting Metrics in Developer Console",
      "Automated Landmark Detection",
      "Original Structure Sensor Support",
      "Dedicated Account Management",
      "TrueDepth Scanning",
      "Apple LiDAR Scanning",
      "Scan Quality Indicator",
      "Special SDK Implementation",
      "510K Clearance Support",
      "Customer Troubleshooting"
    ];

    const headerToKeyMap = {
      "Price Per Month": "price",
      "Number of Apps Supported": "appsSupported",
      "Sensor Scanning": "sensorScanning",
      "Reporting Metrics in Developer Console": "reportingMetrics",
      "Automated Landmark Detection": "anthropometrics",
      "Original Structure Sensor Support": "st01Support",
      "Dedicated Account Management": "accountManagement",
      "TrueDepth Scanning": "truedepthScanning",
      "Apple LiDAR Scanning": "lidarScanning",
      "Scan Quality Indicator": "sqi",
      "Special SDK Implementation": "specialSdkImplementation",
      "510K Clearance Support": "510kSupport",
      "Customer Troubleshooting": "customerTroubleshooting"
    };

    const headerTooltipCopy = {
      "Number of Apps Supported": "The number of apps permitted to utilize the SDK",
      "Sensor Scanning": "Compatible with Structure Sensor 3 and previous model Structure Sensor Pro / (Mark II)",
      "Automated Landmark Detection": "Measurement for specific parts of the body",
      "TrueDepth Scanning": "Front camera scanning on iPhone / iPad",
      "Apple LiDAR Scanning": "Back camera scanning on iPhone / iPad",
      "Customer Troubleshooting": "In-field customer support with our team of 3D professionals",
      "Scan Quality Indicator": "Guiding you to better scans"
    }

    const getFeaturesPlusText = (currentTier) => {
      const tiers = ["Basic", "Professional", "Premium", "Enterprise"];
      const currentIndex = tiers.indexOf(currentTier);
      if (currentIndex > 0) {
        return `${tiers[currentIndex - 1]} Features Plus:`;
      }
      return "";
    };

    const selectedTier = ref(store.state.selectedTier || null);
    const continueButton = ref(null);

    const selectTier = (tier) => {
      selectedTier.value = tier;
      nextTick(() => {
        continueButton.value.$el.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      });
    };

    const handleContinue = () => {
      store.commit('SET_SELECTED_TIER', selectedTier.value);
      router.push('/console/onboarding/billing');
    };

    return {
      subscriptionTiers,
      compareTableData,
      compareTableHeaders,
      headerToKeyMap,
      headerTooltipCopy,
      getFeaturesPlusText,
      selectedTier,
      continueButton,
      selectTier,
      handleContinue
    }
  }
};
</script>

<style scoped lang="scss">
    @import '@/assets/styles/StrButtons.scss'
    @import '@/assets/styles/StrVariables.scss'
    @import '@/assets/styles/SubscriptionTierSelection.scss'
</style>